/**
 * Cross-subdomain cookie utilities shared by storefront apps.
 *
 * After a successful order the checkout app sets `te_clear_cart=true` (and
 * optionally `te_order_id`) on the parent domain so the layout app can clear
 * cart state when the user navigates back via bfcache or a direct link.
 */

export const CLEAR_CART_COOKIE = 'te_clear_cart';
export const CLEAR_CART_ORDER_COOKIE = 'te_order_id';

/**
 * Returns the parent domain for cross-subdomain cookies (e.g. `.example.com`).
 * Returns `null` for localhost / bare IP addresses so cookies stay
 * host-only during local development.
 */
export function getParentDomain(): string | null {
  if (typeof window === 'undefined') return null;
  const { hostname } = window.location;
  if (hostname === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
    return null;
  }
  const parts = hostname.split('.');
  if (parts.length < 2) return null;
  return `.${parts.slice(-2).join('.')}`;
}

/** Read a cookie value by name. Returns `undefined` when not present. */
export function getCookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : undefined;
}

/** Delete a cookie on both the current host and the parent domain. */
export function deleteCookie(name: string): void {
  if (typeof document === 'undefined') return;
  const domain = getParentDomain();
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
  if (domain) {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${domain}`;
  }
}

/** Write a cookie on the parent domain (or current host if localhost). */
export function setCookie(name: string, value: string, maxAgeSec = 3600): void {
  if (typeof document === 'undefined') return;
  const domain = getParentDomain();
  const domainPart = domain ? `; domain=${domain}` : '';
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/${domainPart}; max-age=${maxAgeSec}; SameSite=Lax`;
}

/**
 * Set the cross-subdomain signal cookies that tell the layout app to clear
 * the cart when the user returns from checkout.
 */
export function setClearCartCookie(orderId?: string): void {
  if (typeof document === 'undefined') return;
  const domain = getParentDomain();
  const domainPart = domain ? `; domain=${domain}` : '';
  const maxAge = 3600;

  document.cookie = `${CLEAR_CART_COOKIE}=true; path=/${domainPart}; max-age=${maxAge}; SameSite=Lax`;

  if (orderId) {
    document.cookie = `${CLEAR_CART_ORDER_COOKIE}=${encodeURIComponent(orderId)}; path=/${domainPart}; max-age=${maxAge}; SameSite=Lax`;
  }
}
